/**
 * Phase 15 Plan 15-02 — MonthlyDocumentUploadPage
 *
 * Route: /logsheets/monthly-documents/upload
 * Permission gate: logsheet.monthly.upload-documents
 *
 * Lists eligible (machine, month) tuples and lets the user trigger the
 * UploadMonthlyDocumentsDialog for each tuple.
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, type JSX } from 'react';
import { Navigate } from 'react-router-dom';

import { UploadMonthlyDocumentsDialog } from '@/components/monthly/UploadMonthlyDocumentsDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEligibleForMonthlyDocumentQuery } from '@/hooks/useLogsheetMonthlyQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { EligibleMonthlyTuple, UploadMonthlyDocumentsResult } from '@/types/logsheet-monthly';

export function MonthlyDocumentUploadPage(): JSX.Element {
  const { permissions } = useAuth();
  const [dialogTuple, setDialogTuple] = useState<EligibleMonthlyTuple | null>(null);

  const eligibleQuery = useEligibleForMonthlyDocumentQuery();

  const columns = useMemo<ColumnDef<EligibleMonthlyTuple>[]>(
    () => [
      {
        accessorKey: 'machineCode',
        header: 'Machine Code',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.machineCode}</span>
        ),
      },
      {
        accessorKey: 'machineName',
        header: 'Machine Name',
        cell: ({ row }) => (
          <span>{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</span>
        ),
      },
      {
        accessorKey: 'month',
        header: 'Month',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.month.slice(0, 7)}</span>
        ),
      },
      {
        accessorKey: 'approvedEntryCount',
        header: 'Approved Entries',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.approvedEntryCount}</span>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        cell: ({ row }) => (
          <Button
            type="button"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDialogTuple(row.original);
            }}
            data-testid={`monthly-eligible-upload-trigger-${String(row.original.machineId)}-${row.original.month.slice(0, 7)}`}
          >
            Upload Documents
          </Button>
        ),
      },
    ],
    [],
  );

  const data = eligibleQuery.data ?? [];

  const table = useReactTable({
    data: data as EligibleMonthlyTuple[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.monthly.upload-documents')) {
    return <Navigate to="/dashboard" replace />;
  }

  function handleUploadSuccess(_result: UploadMonthlyDocumentsResult): void {
    setDialogTuple(null);
  }

  return (
    <div className="space-y-6" data-testid="monthly-document-upload-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Monthly Documents</h1>
        <p className="text-muted-foreground">
          Upload Combined Daily and Monthly Summary PDFs to lock a month.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Eligible months</CardTitle>
          <CardDescription>
            These (machine, month) tuples have all entries approved and are ready to lock.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {eligibleQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="monthly-document-upload-loading">
              Loading eligible months…
            </p>
          )}
          {eligibleQuery.isError && (
            <p className="text-sm text-destructive" data-testid="monthly-document-upload-error">
              Failed to load eligible months.
            </p>
          )}

          {eligibleQuery.data && (
            <>
              {data.length === 0 ? (
                <div className="py-8 text-center" data-testid="monthly-document-upload-empty-state">
                  <p className="text-sm text-muted-foreground">
                    No eligible months — all approved months have been locked already, or no months
                    have all-Approved entries yet.
                  </p>
                </div>
              ) : (
                <Table data-testid="monthly-document-upload-table">
                  <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id}>
                        {hg.headers.map((h) => (
                          <TableHead key={h.id}>
                            {h.isPlaceholder
                              ? null
                              : flexRender(h.column.columnDef.header, h.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`monthly-eligible-row-${String(row.original.machineId)}-${row.original.month.slice(0, 7)}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {dialogTuple !== null && (
        <UploadMonthlyDocumentsDialog
          open={true}
          tuple={dialogTuple}
          onClose={() => {
            setDialogTuple(null);
          }}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
}
